// src/components/HubSpotContacts.js
import React, { useEffect, useCallback, useState, forwardRef } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
  Button,
  Tooltip,
  MenuItem,
  Pagination,
  Drawer,
  Stack,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Hub as HubSpotIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { motion } from "framer-motion";
import api from "../axiosConfig";
import { trackedUrl } from "../utils/trackedLinks";

const MotionBox = motion(Box);
const MotionRow = motion(TableRow);

/* ---------------- Helpers & Hooks ---------------- */

const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
};

const SnackbarAlert = forwardRef(function SnackbarAlert(props, ref) {
  return <Alert elevation={6} ref={ref} variant="filled" {...props} />;
});

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && String(v).trim() !== "") return v;
  }
  return "";
};

// Access HubSpot properties safely
const prop = (c, key) => {
  const fromProps = c?.properties?.[key];
  const valFromProps =
    typeof fromProps === "object" &&
    fromProps !== null &&
    "value" in fromProps
      ? fromProps.value
      : fromProps;
  return firstNonEmpty(valFromProps, c?.[key]);
};

const normalizePhones = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  const one = String(raw || "").trim();
  return one ? [one] : [];
};

const digitsOnly = (s) => (s || "").replace(/[^\d]/g, "");
const firstValidPhone = (list) =>
  normalizePhones(list).find((p) => digitsOnly(p).length >= 8) || null;

const hasBeenContacted = (c) => {
  const v = firstNonEmpty(
    c.lastactivitydate_iso,
    prop(c, "lastactivitydate"),
    prop(c, "last_contacted_at")
  );
  return Boolean(v);
};

const formatRelative = (t) => {
  if (!t) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) {
    // Fallback for timestamp numbers
    const n = Number(t);
    if (!Number.isNaN(n) && n > 100000) d.setTime(n);
    else return "—";
  }
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return day < 7 ? `${day}d ago` : d.toLocaleDateString();
};

const buildGoogleCalendarLink = ({ title, details, location, guestEmail }) => {
  const base = "https://calendar.google.com/calendar/u/0/r/eventedit";
  const params = new URLSearchParams();
  if (title) params.set("text", title);
  if (details) params.set("details", details);
  if (location) params.set("location", location);
  if (guestEmail) params.set("add", guestEmail);
  return `${base}?${params.toString()}`;
};

/* --------- Fetch Options Helper --------- */
let leadStatusPromise = null;
async function getLeadStatusOptionsOnce() {
  const cached = sessionStorage.getItem("hs:leadStatusOptions");
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {}
  }
  if (!leadStatusPromise) {
    leadStatusPromise = (async () => {
      try {
        const res = await api.get("/marketing/hubspot/lead-status-options");
        const options = res?.data?.options || [];
        sessionStorage.setItem("hs:leadStatusOptions", JSON.stringify(options));
        return options;
      } catch {
        return [];
      }
    })();
  }
  return leadStatusPromise;
}

/* ====================== Right-side Panel ====================== */
function ContactSidePanel({ open, onClose, contact }) {
  if (!contact) return null;

  const name = contact._name;
  const email = contact._email;
  const phones = contact._phones || [];
  const mainPhone = firstValidPhone(phones);
  const company = contact._company !== "—" ? contact._company : "";

  const telHref = mainPhone
    ? trackedUrl("tel", {
        to: mainPhone,
        contact_id: contact.id,
        source: "hubspot_sidepanel",
        contact_name: name,
      })
    : undefined;

  const waHref = mainPhone
    ? trackedUrl("whatsapp", {
        to: mainPhone,
        contact_id: contact.id,
        source: "hubspot_sidepanel",
        contact_name: name,
        text: "Hello!",
      })
    : undefined;

  const emailHref = email
    ? trackedUrl("email", {
        to: email,
        contact_id: contact.id,
        source: "hubspot_sidepanel",
        contact_name: name,
        subject: "Hello",
        body: "",
      })
    : undefined;

  const calendarLink = buildGoogleCalendarLink({
    title: `Meeting with ${name}`,
    details: `Scheduled via HubSpot Contacts`,
    location: company || "Online",
    guestEmail: email || undefined,
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 320, sm: 400 },
          pt: 10,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "primary.light",
            color: "white",
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
            fontSize: 24,
            fontWeight: "bold",
          }}
        >
          {(name || "?").charAt(0)}
        </Box>
        <Typography variant="h6" fontWeight="bold">
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {company}
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
          <Tooltip title="Call">
            <span>
              <IconButton
                href={telHref}
                disabled={!mainPhone}
                color="primary"
                target="_blank"
              >
                <PhoneIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="WhatsApp">
            <span>
              <IconButton
                href={waHref}
                disabled={!mainPhone}
                color="success"
                target="_blank"
              >
                <WhatsAppIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Email">
            <span>
              <IconButton
                href={emailHref}
                disabled={!email}
                color="info"
                target="_blank"
              >
                <EmailIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Schedule">
            <IconButton href={calendarLink} target="_blank" color="warning">
              <EventIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            color: "text.secondary",
            textTransform: "uppercase",
            fontSize: "0.75rem",
            letterSpacing: 1,
          }}
        >
          Contact Info
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body2">{email || "—"}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Phone Numbers
            </Typography>
            {phones.length > 0 ? (
              phones.map((p) => (
                <Chip key={p} label={p} size="small" sx={{ mr: 0.5, mt: 0.5 }} />
              ))
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Lead Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={contact._status || "New"} color="default" size="small" />
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Last Activity
            </Typography>
            <Typography variant="body2">{formatRelative(contact._last)}</Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", textAlign: "right" }}>
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Drawer>
  );
}

/* ====================== Main Component ====================== */
export default function HubSpotContacts() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, type: "info", msg: "" });
  const [openPanel, setOpenPanel] = useState(false);
  const [activeContact, setActiveContact] = useState(null);

  // Filters (Initialize from LocalStorage)
  const [q, setQ] = useState(localStorage.getItem("hubq") || "");
  const [leadStatus, setLeadStatus] = useState(localStorage.getItem("hubstatus") || "");
  const [rows, setRows] = useState(Number(localStorage.getItem("hubrows")) || 25);
  const [page, setPage] = useState(Number(localStorage.getItem("hubpage")) || 0);

  const debouncedQ = useDebounce(q, 500);

  // Create Contact dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    company: "",
    hs_lead_status: "",
  });

  const handleCreateChange = (key) => (e) => {
    setCreateForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const resetCreateForm = () => {
    setCreateForm({
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      company: "",
      hs_lead_status: "",
    });
  };

  // 1. Load Options & Fix "OPEN_DEAL" Bug
  useEffect(() => {
    let ignore = false;
    getLeadStatusOptionsOnce().then((opts) => {
      if (ignore) return;
      setLeadStatusOptions(opts);

      // AUTO-FIX: If LocalStorage has a junk status (like OPEN_DEAL), clear it.
      if (leadStatus && opts.length > 0) {
        const isValid = opts.some((o) => o.value === leadStatus);
        if (!isValid) {
          console.warn(
            `[HubSpotContacts] Resetting invalid status filter: "${leadStatus}"`
          );
          setLeadStatus("");
          localStorage.removeItem("hubstatus");
        }
      }
    });
    return () => {
      ignore = true;
    };
  }, [leadStatus]);

  // 2. Fetch Contacts Logic
  const loadContacts = useCallback(async (pg, size, query, status) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("limit", size);
      params.append("page", pg + 1); // Backend 1-based
      if (query) params.append("q", query);
      if (status) params.append("status", status);

      const res = await api.get(`/marketing/hubspot/contacts?${params.toString()}`);

      const rawData = res.data?.data || res.data?.results || [];
      const totalCount = res.data?.total || 0;

      const normalized = rawData.map((c) => ({
        ...c,
        id: c.id || c.vid,
        _name: firstNonEmpty(prop(c, "firstname"), c.firstname, prop(c, "lastname"), c.lastname)
          ? `${prop(c, "firstname") || c.firstname || ""} ${prop(c, "lastname") || c.lastname || ""}`.trim()
          : prop(c, "company") || c.company || "(No Name)",
        _email: prop(c, "email") || c.email || "",
        _status: prop(c, "hs_lead_status") || c.hs_lead_status || "",
        _company: prop(c, "company") || c.company || "—",
        _last: c.lastactivitydate_iso || prop(c, "lastactivitydate") || c.lastactivitydate,
        _created: c.createdate_iso || prop(c, "createdate") || c.createdate,
        _phones: normalizePhones(
          prop(c, "phone") || prop(c, "mobilephone") || c.phone || c.mobilephone
        ),
      }));

      setContacts(normalized);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      setError("Failed to fetch contacts. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  // 3. Effect: Trigger Load on Filter Change
  useEffect(() => {
    loadContacts(page, rows, debouncedQ, leadStatus);
    localStorage.setItem("hubq", q);
    localStorage.setItem("hubstatus", leadStatus);
    localStorage.setItem("hubrows", rows);
    localStorage.setItem("hubpage", page);
  }, [page, rows, debouncedQ, leadStatus, loadContacts, q]);

  // 4. Update Status Handler (Optimistic UI)
  const handleStatusUpdate = async (contactId, newStatus) => {
    const oldContacts = [...contacts];
    setContacts((prev) =>
      prev.map((c) => (c.id === contactId ? { ...c, _status: newStatus } : c))
    );

    try {
      await api.patch(`/marketing/hubspot/contacts/${contactId}/status`, {
        hs_lead_status: newStatus,
      });
      setSnack({ open: true, type: "success", msg: "HubSpot status updated successfully" });
    } catch (err) {
      console.error(err);
      setContacts(oldContacts);
      setSnack({ open: true, type: "error", msg: "Failed to update status in HubSpot" });
    }
  };

  // Create Contact
  const handleCreateContact = async () => {
    if (!String(createForm.email || "").trim()) {
      setSnack({ open: true, type: "error", msg: "Email is required" });
      return;
    }

    setCreating(true);
    try {
      await api.post("/marketing/hubspot/contacts", {
        firstname: createForm.firstname || null,
        lastname: createForm.lastname || null,
        email: createForm.email.trim(),
        phone: createForm.phone || null,
        company: createForm.company || null,
        hs_lead_status: createForm.hs_lead_status || null,
      });

      setSnack({ open: true, type: "success", msg: "Contact created in HubSpot" });
      setCreateOpen(false);
      resetCreateForm();

      // Refresh list
      loadContacts(page, rows, q, leadStatus);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to create contact";
      setSnack({ open: true, type: "error", msg });
    } finally {
      setCreating(false);
    }
  };

  const handlePageChange = (e, p) => setPage(p - 1);
  const handleRowsChange = (e) => {
    setRows(Number(e.target.value));
    setPage(0);
  };

  const openSideFromRow = (contact) => {
    setActiveContact(contact);
    setOpenPanel(true);
  };

  return (
    <>
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <HubSpotIcon sx={{ color: "#ff7a59", fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                HubSpot Contacts
              </Typography>
              {loading && <Chip label="Loading..." size="small" />}
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Add Contact
              </Button>

              <Tooltip title="Refresh List">
                <IconButton
                  onClick={() => loadContacts(page, rows, q, leadStatus)}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filters Bar */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />

            <TextField
              select
              size="small"
              label="Filter Status"
              value={leadStatus}
              onChange={(e) => {
                setLeadStatus(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              {leadStatusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Rows"
              value={rows}
              onChange={handleRowsChange}
              sx={{ width: 100 }}
            >
              {[10, 25, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Data Table */}
          <TableContainer
            sx={{
              flexGrow: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name / Email</TableCell>
                  <TableCell>Lead Status (Edit)</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Company
                  </TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Created
                  </TableCell>
                  <TableCell>Phones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  [...Array(rows > 10 ? 10 : rows)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Skeleton variant="circular" width={40} height={40} />
                          <Box sx={{ width: "100%" }}>
                            <Skeleton width="60%" />
                            <Skeleton width="40%" />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rectangular" height={30} width={120} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                        <Skeleton width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                        <Skeleton width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : contacts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 5, color: "text.secondary" }}
                    >
                      No contacts found matching your criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((c) => {
                    const mainPhone = firstValidPhone(c._phones);
                    const name = c._name;
                    const company = c._company;
                    const email = c._email;

                    const telHref = mainPhone
                      ? trackedUrl("tel", {
                          to: mainPhone,
                          contact_id: c.id,
                          source: "hubspot_table",
                          contact_name: name,
                        })
                      : undefined;

                    const waHref = mainPhone
                      ? trackedUrl("whatsapp", {
                          to: mainPhone,
                          contact_id: c.id,
                          source: "hubspot_table",
                          contact_name: name,
                          text: "Hello!",
                        })
                      : undefined;

                    const emailHref = email
                      ? trackedUrl("email", {
                          to: email,
                          contact_id: c.id,
                          source: "hubspot_table",
                          contact_name: name,
                          subject: "Hello",
                          body: "",
                        })
                      : undefined;

                    return (
                      <MotionRow
                        key={c.id}
                        hover
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        sx={{
                          position: "relative",
                          "&:hover .hs-actions": { opacity: 1, transform: "translateY(0)" },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {hasBeenContacted(c) && (
                              <Tooltip title="Contacted recently">
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    bgcolor: "success.main",
                                    borderRadius: "50%",
                                    mr: 1,
                                    boxShadow: 1,
                                  }}
                                />
                              </Tooltip>
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={c._status}
                            onChange={(e) => handleStatusUpdate(c.id, e.target.value)}
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            sx={{
                              minWidth: 140,
                              "& .MuiSelect-select": {
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <Typography variant="caption">Select Status</Typography>
                            </MenuItem>
                            {leadStatusOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>

                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                          {company}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontSize="0.85rem">
                            {formatRelative(c._last)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                          <Typography variant="body2" fontSize="0.85rem">
                            {formatRelative(c._created)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ position: "relative" }}>
                          {c._phones.length === 0
                            ? "—"
                            : c._phones
                                .slice(0, 2)
                                .map((p) => (
                                  <Chip key={p} label={p} size="small" sx={{ mr: 0.5 }} />
                                ))}
                          {c._phones.length > 2 && (
                            <Chip label={`+${c._phones.length - 2}`} size="small" />
                          )}

                          <Box
                            className="hs-actions"
                            sx={{
                              position: "absolute",
                              right: 10,
                              top: "50%",
                              mt: -2,
                              bgcolor: "background.paper",
                              borderRadius: 999,
                              px: 1,
                              display: "inline-flex",
                              gap: 0.5,
                              boxShadow: 3,
                              opacity: 0,
                              transform: "translateY(4px)",
                              transition: "all 160ms ease",
                              zIndex: 10,
                            }}
                          >
                            <Tooltip title="Call">
                              <IconButton
                                size="small"
                                href={telHref}
                                disabled={!mainPhone}
                                color="primary"
                                target="_blank"
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="WhatsApp">
                              <IconButton
                                size="small"
                                href={waHref}
                                disabled={!mainPhone}
                                color="success"
                                target="_blank"
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Email">
                              <IconButton
                                size="small"
                                href={emailHref}
                                disabled={!email}
                                color="info"
                                target="_blank"
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSideFromRow(c);
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </MotionRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Pagination */}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ position: "absolute", left: 10, display: { xs: "none", sm: "block" } }}
            >
              Total Contacts: {total}
            </Typography>
            <Pagination
              count={Math.max(1, Math.ceil(total / rows))}
              page={page + 1}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </Paper>
      </MotionBox>

      {/* Slide-out Panel */}
      <ContactSidePanel
        open={openPanel}
        onClose={() => setOpenPanel(false)}
        contact={activeContact}
      />

      {/* Create Contact Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => (!creating ? setCreateOpen(false) : null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Contact</DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              value={createForm.firstname}
              onChange={handleCreateChange("firstname")}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={createForm.lastname}
              onChange={handleCreateChange("lastname")}
              fullWidth
            />
            <TextField
              label="Email *"
              value={createForm.email}
              onChange={handleCreateChange("email")}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={createForm.phone}
              onChange={handleCreateChange("phone")}
              fullWidth
            />
            <TextField
              label="Company"
              value={createForm.company}
              onChange={handleCreateChange("company")}
              fullWidth
            />

            <TextField
              select
              label="Lead Status"
              value={createForm.hs_lead_status}
              onChange={handleCreateChange("hs_lead_status")}
              fullWidth
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {leadStatusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              if (!creating) {
                setCreateOpen(false);
                resetCreateForm();
              }
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateContact} disabled={creating}>
            {creating ? "Saving..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <SnackbarAlert
          severity={snack.type}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.msg}
        </SnackbarAlert>
      </Snackbar>
    </>
  );
}
